/**
 * Soneoversikt-panel: «hvor har jeg plass?» som TALL per sone.
 * Flytende panel øverst til høyre i 2D-canvaset (InsightLegend eier nederst til høyre).
 * Henter data ved åpning (montering) + manuell oppdatering; serveren sorterer
 * med flest ledige lokasjoner først — rekkefølgen beholdes urørt.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { X, RefreshCw } from 'lucide-react';
import { useLayoutEditor } from '../contexts/LayoutEditorContext';
import { useTranslation } from '../../../../../contexts/I18nProvider';
import {
  warehouseLayoutService,
  ServerZoneSummary,
} from '../../../../../services/warehouseLayoutService';

const ZoneSummaryPanel: React.FC = () => {
  const { dispatch, warehouseId } = useLayoutEditor();
  const { t } = useTranslation();

  const [zones, setZones] = useState<ServerZoneSummary[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Panelet kan lukkes (avmonteres) mens en henting pågår — ikke sett state etterpå
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const fetchZones = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await warehouseLayoutService.getZoneSummary(warehouseId);
      if (mountedRef.current) setZones(data);
    } catch (err) {
      console.warn('Kunne ikke hente soneoversikt:', err);
      if (mountedRef.current) {
        setError(
          t('manager.modules.warehouseLayout.zoneSummary.loadFailed', {}, 'Kunne ikke hente soneoversikt')
        );
      }
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, [warehouseId, t]);

  // Hent ved åpning — panelet monteres kun når showZonePanel er på
  useEffect(() => {
    fetchZones();
  }, [fetchZones]);

  const totals = (zones ?? []).reduce(
    (acc, z) => ({
      total: acc.total + z.totalBins,
      empty: acc.empty + z.emptyBins,
      withStock: acc.withStock + z.binsWithStock,
    }),
    { total: 0, empty: 0, withStock: 0 }
  );

  return (
    <div className="absolute top-3 right-3 z-10 w-72 bg-white border border-gray-200 rounded-lg shadow-lg select-none">
      {/* Topplinje: tittel + oppdater + lukk */}
      <div className="flex items-center justify-between gap-2 px-3 py-2 border-b border-gray-100">
        <h4 className="text-sm font-semibold text-gray-800">
          {t('manager.modules.warehouseLayout.zoneSummary.title', {}, 'Soneoversikt')}
        </h4>
        <div className="flex items-center gap-1">
          <button
            onClick={fetchZones}
            disabled={loading}
            className="p-1 rounded text-gray-500 hover:bg-gray-100 hover:text-gray-700 disabled:opacity-40 disabled:cursor-not-allowed"
            title={t('manager.modules.warehouseLayout.zoneSummary.refresh', {}, 'Oppdater')}
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
          </button>
          <button
            onClick={() => dispatch({ type: 'TOGGLE_ZONE_PANEL' })}
            className="p-1 rounded text-gray-500 hover:bg-gray-100 hover:text-gray-700"
            title={t('manager.modules.warehouseLayout.zoneSummary.close', {}, 'Lukk')}
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {/* Feil-linje (beholder ev. gamle data under) */}
      {error && (
        <div className="px-3 py-1.5 text-xs text-red-600">{error}</div>
      )}

      {/* Første lasting uten data */}
      {!zones && !error && (
        <div className="px-3 py-3 text-xs text-gray-500">
          {t('manager.modules.warehouseLayout.zoneSummary.loading', {}, 'Henter…')}
        </div>
      )}

      {zones && zones.length === 0 && (
        <div className="px-3 py-3 text-xs text-gray-500">
          {t('manager.modules.warehouseLayout.zoneSummary.noZones', {}, 'Ingen soner funnet')}
        </div>
      )}

      {zones && zones.length > 0 && (
        <div className="max-h-72 overflow-y-auto">
          <table className="w-full text-xs">
            <thead className="sticky top-0 bg-white">
              <tr className="text-gray-400 border-b border-gray-100">
                <th className="text-left font-medium px-3 py-1.5">
                  {t('manager.modules.warehouseLayout.zoneSummary.colZone', {}, 'Sone')}
                </th>
                <th className="text-right font-medium px-2 py-1.5">
                  {t('manager.modules.warehouseLayout.zoneSummary.colEmpty', {}, 'Ledig')}
                </th>
                <th className="text-right font-medium px-2 py-1.5">
                  {t('manager.modules.warehouseLayout.zoneSummary.colInUse', {}, 'I bruk')}
                </th>
                <th className="text-right font-medium px-3 py-1.5">
                  {t('manager.modules.warehouseLayout.zoneSummary.colTotal', {}, 'Totalt')}
                </th>
              </tr>
            </thead>
            <tbody>
              {/* Server-sortert: flest ledige først — rekkefølgen beholdes */}
              {zones.map(zone => (
                <tr key={zone.stcZoneID} className="border-b border-gray-50 last:border-b-0">
                  <td className="px-3 py-1.5">
                    <span className="font-medium text-gray-800">{zone.stcZoneID}</span>
                    {zone.description && (
                      <span className="block text-[10px] text-gray-400 truncate max-w-[9rem]">
                        {zone.description}
                      </span>
                    )}
                  </td>
                  <td className="text-right px-2 py-1.5 font-bold text-green-600">
                    {zone.emptyBins}
                  </td>
                  <td className="text-right px-2 py-1.5 text-gray-600">{zone.binsWithStock}</td>
                  <td className="text-right px-3 py-1.5 text-gray-600">{zone.totalBins}</td>
                </tr>
              ))}
            </tbody>
            <tfoot className="sticky bottom-0 bg-gray-50">
              <tr className="border-t border-gray-200 font-semibold text-gray-700">
                <td className="px-3 py-1.5">
                  {t('manager.modules.warehouseLayout.zoneSummary.footerSum', {}, 'Sum')}
                </td>
                <td className="text-right px-2 py-1.5 text-green-700">{totals.empty}</td>
                <td className="text-right px-2 py-1.5">{totals.withStock}</td>
                <td className="text-right px-3 py-1.5">{totals.total}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </div>
  );
};

export default ZoneSummaryPanel;
